/**
 * Intelligence Hub Admin Dashboard
 *
 * Admin interface for managing the crypto news intelligence system.
 * Features:
 * - Sources management (CRUD)
 * - Raw items queue monitoring
 * - News moderation and editing
 * - Article drafts workflow
 * - Analytics and metrics
 */

import { useState, useEffect, useCallback } from 'react';
import { Icons } from '../components/Icons';
import './IntelligenceHubAdmin.css';
import type {
  IntelSource,
  IntelRawItem,
  IntelNews,
  IntelArticleDraft,
  SourceTypeSlug,
  ProcessingStatus,
} from '@/types/intelligence';

// Sub-tab types
type IntelTab = 'overview' | 'sources' | 'queue' | 'news' | 'articles' | 'analytics';

// Stats interface
interface IntelStats {
  sources: {
    total: number;
    active: number;
    byType: Record<string, number>;
  };
  rawItems: {
    total: number;
    pending: number;
    processing: number;
    processed: number;
    failed: number;
  };
  news: {
    total: number;
    published: number;
    draft: number;
    byCategory: Record<string, number>;
  };
  articles: {
    total: number;
    draft: number;
    review: number;
    published: number;
  };
  processing: {
    lastRunAt: string | null;
    itemsProcessedToday: number;
    avgProcessingTime: number;
    errorRate: number;
  };
}

// Colors for status badges
const STATUS_COLORS: Record<string, string> = {
  active: '#10b981',
  inactive: '#6b7280',
  error: '#ef4444',
  pending: '#f59e0b',
  processing: '#3b82f6',
  processed: '#10b981',
  failed: '#ef4444',
  draft: '#8b5cf6',
  review: '#f59e0b',
  published: '#10b981',
  archived: '#374151',
};

const SOURCE_TYPE_ICONS: Record<string, keyof typeof Icons> = {
  rss: 'Rss',
  twitter: 'Twitter',
  telegram: 'MessageCircle',
  discord: 'MessageSquare',
  web: 'Globe',
  api: 'Code',
};

interface IntelligenceHubAdminProps {
  adminKey?: string;
}

export function IntelligenceHubAdmin({ adminKey }: IntelligenceHubAdminProps) {
  // State
  const [activeTab, setActiveTab] = useState<IntelTab>('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data state
  const [stats, setStats] = useState<IntelStats | null>(null);
  const [sources, setSources] = useState<IntelSource[]>([]);
  const [rawItems, setRawItems] = useState<IntelRawItem[]>([]);
  const [news, setNews] = useState<IntelNews[]>([]);
  const [articles, setArticles] = useState<IntelArticleDraft[]>([]);

  // Filters
  const [sourceTypeFilter, setSourceTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Modal state
  const [editingSource, setEditingSource] = useState<IntelSource | null>(null);
  const [editingNews, setEditingNews] = useState<IntelNews | null>(null);
  const [showAddSource, setShowAddSource] = useState(false);

  // API helper
  const apiCall = useCallback(async (
    endpoint: string,
    options: RequestInit = {}
  ) => {
    const response = await fetch(endpoint, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminKey}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  }, [adminKey]);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const data = await apiCall('/api/intel/admin?action=stats');
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, [apiCall]);

  // Load sources
  const loadSources = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (sourceTypeFilter !== 'all') params.set('type', sourceTypeFilter);
      if (statusFilter !== 'all') params.set('is_active', statusFilter === 'active' ? 'true' : 'false');
      if (searchQuery) params.set('search', searchQuery);

      const response = await apiCall(`/api/intel/sources?${params.toString()}`);
      // API returns { data: { sources: [...] } }
      setSources(response.data?.sources || response.sources || []);
    } catch (err) {
      console.error('Failed to load sources:', err);
    }
  }, [apiCall, sourceTypeFilter, statusFilter, searchQuery]);

  // Load raw items (queue)
  const loadRawItems = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      params.set('limit', '50');

      const data = await apiCall(`/api/intel/admin?action=queue&${params.toString()}`);
      setRawItems(data.items || []);
    } catch (err) {
      console.error('Failed to load raw items:', err);
    }
  }, [apiCall, statusFilter]);

  // Load news
  const loadNews = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (searchQuery) params.set('search', searchQuery);
      params.set('per_page', '50');
      params.set('include_drafts', 'true');

      const data = await apiCall(`/api/intel/news?${params.toString()}`);
      setNews(data.data?.news || []);
    } catch (err) {
      console.error('Failed to load news:', err);
    }
  }, [apiCall, statusFilter, searchQuery]);

  // Load articles
  const loadArticles = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);

      const data = await apiCall(`/api/intel/admin?action=articles&${params.toString()}`);
      setArticles(data.articles || []);
    } catch (err) {
      console.error('Failed to load articles:', err);
    }
  }, [apiCall, statusFilter]);

  // Load data based on active tab
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        switch (activeTab) {
          case 'overview':
            await loadStats();
            break;
          case 'sources':
            await loadSources();
            break;
          case 'queue':
            await loadRawItems();
            break;
          case 'news':
            await loadNews();
            break;
          case 'articles':
            await loadArticles();
            break;
          case 'analytics':
            await loadStats();
            break;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Une erreur est survenue');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [activeTab, loadStats, loadSources, loadRawItems, loadNews, loadArticles]);

  // Source actions
  const createSource = async (sourceData: Partial<IntelSource>) => {
    try {
      await apiCall('/api/intel/sources', {
        method: 'POST',
        body: JSON.stringify(sourceData),
      });
      await loadSources();
      setShowAddSource(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la création');
    }
  };

  const updateSource = async (id: string, updates: Partial<IntelSource>) => {
    try {
      await apiCall(`/api/intel/sources?id=${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
      await loadSources();
      setEditingSource(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la mise à jour');
    }
  };

  const deleteSource = async (id: string) => {
    if (!confirm('Supprimer cette source ?')) return;

    try {
      await apiCall(`/api/intel/sources?id=${id}`, { method: 'DELETE' });
      await loadSources();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
    }
  };

  // News actions
  const updateNews = async (id: string, updates: Partial<IntelNews>) => {
    try {
      await apiCall(`/api/intel/news?id=${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
      await loadNews();
      setEditingNews(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la mise à jour');
    }
  };

  const deleteNews = async (id: string) => {
    if (!confirm('Supprimer cette news ?')) return;

    try {
      await apiCall(`/api/intel/news?id=${id}`, { method: 'DELETE' });
      await loadNews();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
    }
  };

  // Queue actions
  const retryFailedItem = async (id: string) => {
    try {
      await apiCall('/api/intel/admin', {
        method: 'POST',
        body: JSON.stringify({ action: 'retry-item', id }),
      });
      await loadRawItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  };

  const triggerProcessing = async () => {
    try {
      setLoading(true);
      await apiCall('/api/cron/intel-process', { method: 'POST' });
      await loadStats();
      await loadRawItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const triggerFetching = async () => {
    try {
      setLoading(true);
      await apiCall('/api/cron/intel-fetch', { method: 'POST' });
      await loadStats();
      await loadRawItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  // Format helpers
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Tab definitions
  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: Icons.BarChart2 },
    { id: 'sources', label: 'Sources', icon: Icons.Rss },
    { id: 'queue', label: 'File d\'attente', icon: Icons.Layers },
    { id: 'news', label: 'News', icon: Icons.Newspaper },
    { id: 'articles', label: 'Articles', icon: Icons.FileText },
    { id: 'analytics', label: 'Analytiques', icon: Icons.PieChart },
  ];

  return (
    <div className="intel-admin">
      {/* Header */}
      <div className="intel-admin__header">
        <div className="intel-admin__header-left">
          <div className="intel-admin__icon">
            <Icons.Zap size={24} />
          </div>
          <div>
            <h2>Intelligence Hub</h2>
            <p>Gestion du système de news crypto</p>
          </div>
        </div>
        <div className="intel-admin__header-actions">
          <button
            onClick={triggerFetching}
            disabled={loading}
            className="intel-admin__action-btn"
          >
            <Icons.Download size={16} />
            Récupérer
          </button>
          <button
            onClick={triggerProcessing}
            disabled={loading}
            className="intel-admin__action-btn intel-admin__action-btn--primary"
          >
            <Icons.Zap size={16} />
            Traiter
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="intel-admin__error">
          <Icons.AlertTriangle size={16} />
          {error}
          <button onClick={() => setError(null)}>
            <Icons.X size={14} />
          </button>
        </div>
      )}

      {/* Sub-tabs */}
      <div className="intel-admin__tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as IntelTab)}
            className={`intel-admin__tab ${activeTab === tab.id ? 'active' : ''}`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="intel-admin__content">
        {loading && (
          <div className="intel-admin__loading">
            <Icons.Loader2 size={24} className="animate-spin" />
            <span>Chargement...</span>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && !loading && stats && (
          <div className="intel-admin__overview">
            <div className="intel-admin__stats-grid">
              <StatCard
                title="Sources actives"
                value={stats.sources.active}
                total={stats.sources.total}
                icon={<Icons.Rss size={20} />}
                color="#8b5cf6"
              />
              <StatCard
                title="En attente"
                value={stats.rawItems.pending}
                subtitle="items à traiter"
                icon={<Icons.Clock size={20} />}
                color="#f59e0b"
              />
              <StatCard
                title="News publiées"
                value={stats.news.published}
                total={stats.news.total}
                icon={<Icons.Newspaper size={20} />}
                color="#10b981"
              />
              <StatCard
                title="Traités aujourd'hui"
                value={stats.processing.itemsProcessedToday}
                subtitle={`${stats.processing.errorRate.toFixed(1)}% erreurs`}
                icon={<Icons.Activity size={20} />}
                color="#3b82f6"
              />
            </div>

            <div className="intel-admin__overview-grid">
              {/* Sources by type */}
              <div className="intel-admin__card">
                <h3>Sources par type</h3>
                <div className="intel-admin__bar-chart">
                  {Object.entries(stats.sources.byType).map(([type, count]) => (
                    <div key={type} className="intel-admin__bar-item">
                      <div className="intel-admin__bar-label">
                        {type.toUpperCase()}
                      </div>
                      <div className="intel-admin__bar-wrapper">
                        <div
                          className="intel-admin__bar-fill"
                          style={{
                            width: `${(count / stats.sources.total) * 100}%`,
                            background: '#8b5cf6',
                          }}
                        />
                      </div>
                      <div className="intel-admin__bar-value">{count}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Processing queue status */}
              <div className="intel-admin__card">
                <h3>File de traitement</h3>
                <div className="intel-admin__queue-status">
                  <QueueStatusItem
                    label="En attente"
                    count={stats.rawItems.pending}
                    color={STATUS_COLORS.pending}
                  />
                  <QueueStatusItem
                    label="En cours"
                    count={stats.rawItems.processing}
                    color={STATUS_COLORS.processing}
                  />
                  <QueueStatusItem
                    label="Traités"
                    count={stats.rawItems.processed}
                    color={STATUS_COLORS.processed}
                  />
                  <QueueStatusItem
                    label="Échoués"
                    count={stats.rawItems.failed}
                    color={STATUS_COLORS.failed}
                  />
                </div>
                {stats.processing.lastRunAt && (
                  <p className="intel-admin__last-run">
                    Dernier traitement: {formatDate(stats.processing.lastRunAt)}
                  </p>
                )}
              </div>

              {/* News by category */}
              <div className="intel-admin__card">
                <h3>News par catégorie</h3>
                <div className="intel-admin__category-list">
                  {Object.entries(stats.news.byCategory)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 8)
                    .map(([category, count]) => (
                      <div key={category} className="intel-admin__category-item">
                        <span className="intel-admin__category-name">{category}</span>
                        <span className="intel-admin__category-count">{count}</span>
                      </div>
                    ))}
                </div>
              </div>

              {/* Articles status */}
              <div className="intel-admin__card">
                <h3>Articles</h3>
                <div className="intel-admin__articles-status">
                  <div className="intel-admin__article-stat">
                    <span className="intel-admin__article-count">{stats.articles.draft}</span>
                    <span className="intel-admin__article-label">Brouillons</span>
                  </div>
                  <div className="intel-admin__article-stat">
                    <span className="intel-admin__article-count">{stats.articles.review}</span>
                    <span className="intel-admin__article-label">En révision</span>
                  </div>
                  <div className="intel-admin__article-stat">
                    <span className="intel-admin__article-count">{stats.articles.published}</span>
                    <span className="intel-admin__article-label">Publiés</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sources Tab */}
        {activeTab === 'sources' && !loading && (
          <div className="intel-admin__sources">
            {/* Filters */}
            <div className="intel-admin__filters">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher une source..."
                className="intel-admin__search"
              />
              <select
                aria-label="Filter by source type"
                value={sourceTypeFilter}
                onChange={(e) => setSourceTypeFilter(e.target.value)}
                className="intel-admin__filter"
              >
                <option value="all">Tous les types</option>
                <option value="rss">RSS</option>
                <option value="twitter">Twitter</option>
                <option value="telegram">Telegram</option>
                <option value="web">Web</option>
                <option value="api">API</option>
              </select>
              <select
                aria-label="Filter by status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="intel-admin__filter"
              >
                <option value="all">Tous les statuts</option>
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
                <option value="error">Erreur</option>
              </select>
              <button
                onClick={() => setShowAddSource(true)}
                className="intel-admin__action-btn intel-admin__action-btn--primary"
              >
                <Icons.Plus size={16} />
                Ajouter
              </button>
            </div>

            {/* Sources list */}
            <div className="intel-admin__sources-grid">
              {sources.map((source) => (
                <SourceCard
                  key={source.id}
                  source={source}
                  onEdit={() => setEditingSource(source)}
                  onDelete={() => deleteSource(source.id)}
                  onToggle={() => updateSource(source.id, { is_active: !source.is_active })}
                />
              ))}
              {sources.length === 0 && (
                <div className="intel-admin__empty">
                  <Icons.Rss size={40} />
                  <p>Aucune source trouvée</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Queue Tab */}
        {activeTab === 'queue' && !loading && (
          <div className="intel-admin__queue">
            {/* Filters */}
            <div className="intel-admin__filters">
              <select
                aria-label="Filter by status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="intel-admin__filter"
              >
                <option value="all">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="processing">En cours</option>
                <option value="processed">Traités</option>
                <option value="failed">Échoués</option>
              </select>
              <button onClick={loadRawItems} className="intel-admin__action-btn">
                <Icons.RefreshCw size={16} />
                Rafraîchir
              </button>
            </div>

            {/* Queue table */}
            <div className="intel-admin__table-wrapper">
              <table className="intel-admin__table">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Titre</th>
                    <th>Statut</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rawItems.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <span className="intel-admin__source-badge">
                          {item.source_id}
                        </span>
                      </td>
                      <td>
                        <div className="intel-admin__item-title">
                          {item.title || item.content_raw?.substring(0, 50) + '...'}
                        </div>
                        {item.url && (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="intel-admin__item-url"
                          >
                            {new URL(item.url).hostname}
                          </a>
                        )}
                      </td>
                      <td>
                        <span
                          className="intel-admin__status-badge"
                          style={{
                            background: `${STATUS_COLORS[item.processing_status]}20`,
                            color: STATUS_COLORS[item.processing_status],
                          }}
                        >
                          {item.processing_status}
                        </span>
                      </td>
                      <td>{formatDate(item.fetched_at)}</td>
                      <td>
                        {item.processing_status === 'failed' && (
                          <button
                            onClick={() => retryFailedItem(item.id)}
                            className="intel-admin__icon-btn"
                            title="Réessayer"
                          >
                            <Icons.RefreshCw size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rawItems.length === 0 && (
                <div className="intel-admin__empty">
                  <Icons.Inbox size={40} />
                  <p>File d'attente vide</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* News Tab */}
        {activeTab === 'news' && !loading && (
          <div className="intel-admin__news">
            {/* Filters */}
            <div className="intel-admin__filters">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher..."
                className="intel-admin__search"
              />
              <select
                aria-label="Filter by status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="intel-admin__filter"
              >
                <option value="all">Tous les statuts</option>
                <option value="published">Publiées</option>
                <option value="draft">Brouillons</option>
              </select>
              <button onClick={loadNews} className="intel-admin__action-btn">
                <Icons.RefreshCw size={16} />
                Rafraîchir
              </button>
            </div>

            {/* News table */}
            <div className="intel-admin__table-wrapper">
              <table className="intel-admin__table">
                <thead>
                  <tr>
                    <th>Titre</th>
                    <th>Catégorie</th>
                    <th>Importance</th>
                    <th>Sentiment</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {news.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div className="intel-admin__news-title">
                          {item.is_breaking && (
                            <span className="intel-admin__breaking-badge">
                              <Icons.Zap size={12} />
                            </span>
                          )}
                          {item.title}
                        </div>
                        <div className="intel-admin__news-tokens">
                          {item.tokens?.slice(0, 3).map((token) => (
                            <span key={token} className="intel-admin__token">
                              ${token}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <span className="intel-admin__category-badge">
                          {item.category}
                        </span>
                      </td>
                      <td>
                        <div className="intel-admin__importance">
                          <div
                            className="intel-admin__importance-bar"
                            style={{ width: `${item.importance_score * 100}%` }}
                          />
                          <span>{Math.round(item.importance_score * 100)}</span>
                        </div>
                      </td>
                      <td>
                        <span
                          className="intel-admin__sentiment-badge"
                          data-sentiment={item.sentiment}
                        >
                          {item.sentiment === 'bullish' && <Icons.TrendingUp size={14} />}
                          {item.sentiment === 'bearish' && <Icons.TrendingDown size={14} />}
                          {item.sentiment === 'neutral' && <Icons.Minus size={14} />}
                          {item.sentiment}
                        </span>
                      </td>
                      <td>{formatDate(item.published_at)}</td>
                      <td>
                        <div className="intel-admin__actions">
                          <button
                            onClick={() => setEditingNews(item)}
                            className="intel-admin__icon-btn"
                            title="Modifier"
                          >
                            <Icons.Edit size={14} />
                          </button>
                          <button
                            onClick={() => deleteNews(item.id)}
                            className="intel-admin__icon-btn intel-admin__icon-btn--danger"
                            title="Supprimer"
                          >
                            <Icons.Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {news.length === 0 && (
                <div className="intel-admin__empty">
                  <Icons.Newspaper size={40} />
                  <p>Aucune news trouvée</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Articles Tab */}
        {activeTab === 'articles' && !loading && (
          <div className="intel-admin__articles">
            {/* Filters */}
            <div className="intel-admin__filters">
              <select
                aria-label="Filter by status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="intel-admin__filter"
              >
                <option value="all">Tous les statuts</option>
                <option value="draft">Brouillons</option>
                <option value="review">En révision</option>
                <option value="published">Publiés</option>
                <option value="archived">Archivés</option>
              </select>
              <button onClick={loadArticles} className="intel-admin__action-btn">
                <Icons.RefreshCw size={16} />
                Rafraîchir
              </button>
            </div>

            {/* Articles grid */}
            <div className="intel-admin__articles-grid">
              {articles.map((article) => (
                <div key={article.id} className="intel-admin__article-card">
                  <div className="intel-admin__article-header">
                    <span
                      className="intel-admin__status-badge"
                      style={{
                        background: `${STATUS_COLORS[article.status]}20`,
                        color: STATUS_COLORS[article.status],
                      }}
                    >
                      {article.status}
                    </span>
                    <span className="intel-admin__article-date">
                      {formatDate(article.created_at)}
                    </span>
                  </div>
                  <h4 className="intel-admin__article-title">{article.title}</h4>
                  {article.summary && (
                    <p className="intel-admin__article-summary">{article.summary}</p>
                  )}
                  <div className="intel-admin__article-meta">
                    {article.author_name && (
                      <span>Par {article.author_name}</span>
                    )}
                    {article.word_count && (
                      <span>{article.word_count} mots</span>
                    )}
                  </div>
                  <div className="intel-admin__article-actions">
                    <button className="intel-admin__icon-btn">
                      <Icons.Eye size={14} />
                    </button>
                    <button className="intel-admin__icon-btn">
                      <Icons.Edit size={14} />
                    </button>
                    {article.status === 'draft' && (
                      <button className="intel-admin__icon-btn intel-admin__icon-btn--success">
                        <Icons.Send size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {articles.length === 0 && (
                <div className="intel-admin__empty">
                  <Icons.FileText size={40} />
                  <p>Aucun article trouvé</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && !loading && stats && (
          <div className="intel-admin__analytics">
            <div className="intel-admin__analytics-grid">
              {/* Processing metrics */}
              <div className="intel-admin__card intel-admin__card--large">
                <h3>Métriques de traitement</h3>
                <div className="intel-admin__metrics">
                  <div className="intel-admin__metric">
                    <span className="intel-admin__metric-value">
                      {stats.processing.avgProcessingTime.toFixed(1)}s
                    </span>
                    <span className="intel-admin__metric-label">
                      Temps moyen de traitement
                    </span>
                  </div>
                  <div className="intel-admin__metric">
                    <span className="intel-admin__metric-value">
                      {stats.processing.errorRate.toFixed(1)}%
                    </span>
                    <span className="intel-admin__metric-label">
                      Taux d'erreur
                    </span>
                  </div>
                  <div className="intel-admin__metric">
                    <span className="intel-admin__metric-value">
                      {stats.processing.itemsProcessedToday}
                    </span>
                    <span className="intel-admin__metric-label">
                      Items traités aujourd'hui
                    </span>
                  </div>
                </div>
              </div>

              {/* Source health */}
              <div className="intel-admin__card">
                <h3>Santé des sources</h3>
                <div className="intel-admin__health-grid">
                  <div className="intel-admin__health-item">
                    <div
                      className="intel-admin__health-indicator"
                      style={{ background: '#10b981' }}
                    />
                    <span>Actives</span>
                    <strong>{stats.sources.active}</strong>
                  </div>
                  <div className="intel-admin__health-item">
                    <div
                      className="intel-admin__health-indicator"
                      style={{ background: '#6b7280' }}
                    />
                    <span>Inactives</span>
                    <strong>{stats.sources.total - stats.sources.active}</strong>
                  </div>
                </div>
              </div>

              {/* Content stats */}
              <div className="intel-admin__card">
                <h3>Contenu</h3>
                <div className="intel-admin__content-stats">
                  <div className="intel-admin__content-stat">
                    <Icons.Newspaper size={20} />
                    <div>
                      <strong>{formatNumber(stats.news.total)}</strong>
                      <span>News total</span>
                    </div>
                  </div>
                  <div className="intel-admin__content-stat">
                    <Icons.FileText size={20} />
                    <div>
                      <strong>{formatNumber(stats.articles.total)}</strong>
                      <span>Articles total</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Source Modal */}
      {showAddSource && (
        <SourceModal
          onClose={() => setShowAddSource(false)}
          onSave={createSource}
        />
      )}

      {/* Edit Source Modal */}
      {editingSource && (
        <SourceModal
          source={editingSource}
          onClose={() => setEditingSource(null)}
          onSave={(data) => updateSource(editingSource.id, data)}
        />
      )}

      {/* Edit News Modal */}
      {editingNews && (
        <NewsModal
          news={editingNews}
          onClose={() => setEditingNews(null)}
          onSave={(data) => updateNews(editingNews.id, data)}
        />
      )}
    </div>
  );
}

// Sub-components

function StatCard({
  title,
  value,
  total,
  subtitle,
  icon,
  color,
}: {
  title: string;
  value: number;
  total?: number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
}) {
  return (
    <div className="intel-admin__stat-card" style={{ borderTopColor: color }}>
      <div className="intel-admin__stat-icon" style={{ background: `${color}20`, color }}>
        {icon}
      </div>
      <div className="intel-admin__stat-content">
        <div className="intel-admin__stat-value">
          {value}
          {total && <span className="intel-admin__stat-total">/{total}</span>}
        </div>
        <div className="intel-admin__stat-title">{title}</div>
        {subtitle && <div className="intel-admin__stat-subtitle">{subtitle}</div>}
      </div>
    </div>
  );
}

function QueueStatusItem({
  label,
  count,
  color,
}: {
  label: string;
  count: number;
  color: string;
}) {
  return (
    <div className="intel-admin__queue-item">
      <div className="intel-admin__queue-dot" style={{ background: color }} />
      <span className="intel-admin__queue-label">{label}</span>
      <span className="intel-admin__queue-count" style={{ color }}>{count}</span>
    </div>
  );
}

function SourceCard({
  source,
  onEdit,
  onDelete,
  onToggle,
}: {
  source: IntelSource;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: () => void;
}) {
  const iconName = SOURCE_TYPE_ICONS[source.type_slug] || 'Globe';
  const IconComponent = Icons[iconName] || Icons.Globe;

  return (
    <div className={`intel-admin__source-card ${!source.is_active ? 'inactive' : ''}`}>
      <div className="intel-admin__source-header">
        <div className="intel-admin__source-icon">
          <IconComponent size={20} />
        </div>
        <div className="intel-admin__source-info">
          <h4>{source.name}</h4>
          <span className="intel-admin__source-type">{source.type_slug}</span>
        </div>
        <label className="admin-toggle">
          <input
            type="checkbox"
            checked={source.is_active}
            onChange={onToggle}
          />
          <span className="admin-toggle__slider" />
        </label>
      </div>

      <div className="intel-admin__source-url">
        <Icons.Link size={12} />
        <span>{source.url}</span>
      </div>

      <div className="intel-admin__source-meta">
        <div className="intel-admin__source-stat">
          <span className="intel-admin__source-stat-value">
            {source.reliability_score.toFixed(1)}
          </span>
          <span className="intel-admin__source-stat-label">Fiabilité</span>
        </div>
        <div className="intel-admin__source-stat">
          <span className="intel-admin__source-stat-value">
            {source.fetch_interval_minutes}m
          </span>
          <span className="intel-admin__source-stat-label">Intervalle</span>
        </div>
        {source.last_fetch_at && (
          <div className="intel-admin__source-stat">
            <span className="intel-admin__source-stat-value">
              {new Date(source.last_fetch_at).toLocaleTimeString('fr-FR', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
            <span className="intel-admin__source-stat-label">Dernier fetch</span>
          </div>
        )}
      </div>

      {source.error_count > 0 && (
        <div className="intel-admin__source-error">
          <Icons.AlertTriangle size={14} />
          {source.error_count} erreurs
          {source.last_error && (
            <span className="intel-admin__source-error-msg">
              : {source.last_error.substring(0, 50)}...
            </span>
          )}
        </div>
      )}

      <div className="intel-admin__source-actions">
        <button onClick={onEdit} className="intel-admin__icon-btn">
          <Icons.Edit size={14} />
        </button>
        <button onClick={onDelete} className="intel-admin__icon-btn intel-admin__icon-btn--danger">
          <Icons.Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// Source type mapping for the form
const SOURCE_TYPE_OPTIONS = [
  { slug: 'media_major', label: 'Média majeur', method: 'rss' },
  { slug: 'media_specialized', label: 'Média spécialisé', method: 'rss' },
  { slug: 'official_blog', label: 'Blog officiel', method: 'rss' },
  { slug: 'official_twitter', label: 'Twitter officiel', method: 'api' },
  { slug: 'twitter_influencer', label: 'Influenceur Twitter', method: 'api' },
  { slug: 'telegram_channel', label: 'Channel Telegram', method: 'api' },
  { slug: 'newsletter', label: 'Newsletter', method: 'rss' },
  { slug: 'aggregator', label: 'Agrégateur', method: 'rss' },
];

function SourceModal({
  source,
  onClose,
  onSave,
}: {
  source?: IntelSource;
  onClose: () => void;
  onSave: (data: Partial<IntelSource>) => void;
}) {
  const [formData, setFormData] = useState({
    name: source?.name || '',
    type_slug: source?.type?.slug || 'media_specialized',
    identifier: source?.identifier || '',
    description: source?.description || '',
    website_url: source?.website_url || '',
    fetch_frequency_minutes: source?.fetch_frequency_minutes || 30,
    primary_tokens: source?.primary_tokens || [],
    language: source?.language || 'en',
    is_active: source?.is_active ?? true,
  });

  const [tokensInput, setTokensInput] = useState(formData.primary_tokens.join(', '));

  const selectedType = SOURCE_TYPE_OPTIONS.find(t => t.slug === formData.type_slug);
  const isRSS = selectedType?.method === 'rss';
  const isTwitter = formData.type_slug.includes('twitter');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Build config based on type
    let config: any;
    if (isRSS) {
      config = { method: 'rss', rss_url: formData.identifier };
    } else if (isTwitter) {
      config = { method: 'api', platform: 'twitter', handle: formData.identifier.replace('@', ''), use_nitter: true };
    } else {
      config = { method: 'api' };
    }

    onSave({
      name: formData.name,
      type_slug: formData.type_slug,
      identifier: formData.identifier,
      description: formData.description,
      website_url: formData.website_url,
      fetch_frequency_minutes: formData.fetch_frequency_minutes,
      config,
      primary_tokens: tokensInput.split(',').map(t => t.trim().toUpperCase()).filter(Boolean),
      language: formData.language,
      is_active: formData.is_active,
    });
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal__header">
          <h3>{source ? 'Modifier la source' : 'Ajouter une source'}</h3>
          <button onClick={onClose}>
            <Icons.X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="admin-modal__body">
          <div className="intel-admin__form-group">
            <label>Nom</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="CoinDesk, Vitalik Buterin..."
              required
            />
          </div>

          <div className="intel-admin__form-group">
            <label>Type de source</label>
            <select
              aria-label="Type de source"
              value={formData.type_slug}
              onChange={(e) => setFormData({ ...formData, type_slug: e.target.value as SourceTypeSlug })}
            >
              {SOURCE_TYPE_OPTIONS.map(type => (
                <option key={type.slug} value={type.slug}>{type.label}</option>
              ))}
            </select>
          </div>

          <div className="intel-admin__form-group">
            <label>
              {isRSS ? 'URL du flux RSS' :
               isTwitter ? 'Handle Twitter (@...)' : 'Identifiant'}
            </label>
            <input
              type={isRSS ? 'url' : 'text'}
              value={formData.identifier}
              onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
              placeholder={isRSS ? 'https://example.com/feed.xml' : '@vitalikbuterin'}
              required
            />
          </div>

          <div className="intel-admin__form-group">
            <label>Description (optionnel)</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description courte de la source"
            />
          </div>

          <div className="intel-admin__form-row">
            <div className="intel-admin__form-group">
              <label>Fréquence de fetch (min)</label>
              <input
                type="number"
                min="5"
                max="1440"
                value={formData.fetch_frequency_minutes}
                onChange={(e) => setFormData({ ...formData, fetch_frequency_minutes: parseInt(e.target.value) })}
              />
            </div>

            <div className="intel-admin__form-group">
              <label>Langue</label>
              <select
                aria-label="Langue"
                value={formData.language}
                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
              >
                <option value="en">Anglais</option>
                <option value="fr">Français</option>
              </select>
            </div>
          </div>

          <div className="intel-admin__form-group">
            <label>Tokens principaux (séparés par virgule)</label>
            <input
              type="text"
              value={tokensInput}
              onChange={(e) => setTokensInput(e.target.value)}
              placeholder="BTC, ETH, SOL..."
            />
          </div>

          <div className="intel-admin__form-group">
            <label className="intel-admin__checkbox-label">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              Source active
            </label>
          </div>

          <div className="intel-admin__form-actions">
            <button type="button" onClick={onClose} className="intel-admin__btn">
              Annuler
            </button>
            <button type="submit" className="intel-admin__btn intel-admin__btn--primary">
              {source ? 'Enregistrer' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NewsModal({
  news,
  onClose,
  onSave,
}: {
  news: IntelNews;
  onClose: () => void;
  onSave: (data: Partial<IntelNews>) => void;
}) {
  const [formData, setFormData] = useState({
    title: news.title,
    summary: news.summary || '',
    content: news.content || '',
    category: news.category,
    sentiment: news.sentiment,
    importance_score: news.importance_score,
    is_breaking: news.is_breaking,
    tokens: news.tokens || [],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal admin-modal--large" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal__header">
          <h3>Modifier la news</h3>
          <button onClick={onClose}>
            <Icons.X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="admin-modal__body">
          <div className="intel-admin__form-group">
            <label>Titre</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>

          <div className="intel-admin__form-group">
            <label>Résumé</label>
            <textarea
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              rows={3}
            />
          </div>

          <div className="intel-admin__form-row">
            <div className="intel-admin__form-group">
              <label>Catégorie</label>
              <select
                aria-label="Catégorie"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                <option value="market">Market</option>
                <option value="defi">DeFi</option>
                <option value="nft">NFT</option>
                <option value="regulation">Regulation</option>
                <option value="technology">Technology</option>
                <option value="adoption">Adoption</option>
                <option value="security">Security</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="intel-admin__form-group">
              <label>Sentiment</label>
              <select
                aria-label="Sentiment"
                value={formData.sentiment}
                onChange={(e) => setFormData({ ...formData, sentiment: e.target.value as any })}
              >
                <option value="bullish">Bullish</option>
                <option value="bearish">Bearish</option>
                <option value="neutral">Neutral</option>
              </select>
            </div>
          </div>

          <div className="intel-admin__form-row">
            <div className="intel-admin__form-group">
              <label>Importance (0-1)</label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.1"
                value={formData.importance_score}
                onChange={(e) => setFormData({ ...formData, importance_score: parseFloat(e.target.value) })}
              />
            </div>

            <div className="intel-admin__form-group">
              <label className="intel-admin__checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.is_breaking}
                  onChange={(e) => setFormData({ ...formData, is_breaking: e.target.checked })}
                />
                Breaking News
              </label>
            </div>
          </div>

          <div className="intel-admin__form-group">
            <label>Tokens (séparés par virgule)</label>
            <input
              type="text"
              value={formData.tokens.join(', ')}
              onChange={(e) => setFormData({
                ...formData,
                tokens: e.target.value.split(',').map(t => t.trim()).filter(Boolean),
              })}
            />
          </div>

          <div className="intel-admin__form-actions">
            <button type="button" onClick={onClose} className="intel-admin__btn">
              Annuler
            </button>
            <button type="submit" className="intel-admin__btn intel-admin__btn--primary">
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default IntelligenceHubAdmin;
